import { useCallback } from "react";
import type { Speaker } from "../../event-experience/schemas";
import { sortSpeakers } from "../../event-experience/speaker-order";
import { useAdminSpeakers } from "../use-admin-content";
import { useAttendeeRecords } from "../use-attendee-records";

import { speakerSpec } from "./content-specs";
import { EntityCrudSection, type LookupOption } from "./EntityCrudSection";

/**
 * Speakers: the shared CRUD form, plus the roster the `attendeeId` field picks
 * from.
 *
 * It has its own wrapper rather than living in `ContentTab` for one reason:
 * the roster is the largest collection in the event, and ContentTab runs every
 * one of its hooks on every content tab. Reading hundreds of attendee rows
 * while somebody edits a track is a cost nobody asked for.
 *
 * `attendeeId` needs no rules change — create and update already carry it, and
 * `hasLiveSpeakerAttendee` already validates it. Only the input was missing,
 * which is why every speaker in production is unlinked and why the agenda
 * presenter picker's "already on the roster" filter has never excluded anyone.
 */
export function SpeakersSection({
  eventId,
  uid,
}: {
  eventId: string;
  uid: string;
}) {
  const speakers = useAdminSpeakers(eventId);
  const attendees = useAttendeeRecords(eventId);

  const linkedBy = new Map(
    (speakers.status === "success" ? speakers.data : [])
      .filter((speaker): speaker is Speaker & { attendeeId: string } =>
        Boolean(speaker.attendeeId),
      )
      .map((speaker) => [
        speaker.attendeeId,
        { id: speaker.id, name: speaker.name },
      ]),
  );

  const options: LookupOption[] = (
    attendees.status === "success" ? attendees.data : []
  ).map((attendee) => ({
    id: attendee.id,
    label: attendee.name,
    detail: [attendee.role, attendee.company].filter(Boolean).join(", "),
    // Typing a roster person's name, job title and company a second time is
    // the friction that keeps speakers unlinked. Only blank fields are
    // filled, so this never overwrites what the organiser wrote.
    fills: {
      name: attendee.name,
      role: attendee.role ?? "",
      company: attendee.company ?? "",
      bio: attendee.bio ?? "",
      avatar: attendee.avatar ?? "",
    },
  }));

  // Stable, because EntityCrudSection's post-command effect depends on this
  // identity and its body calls it while a command sits at succeeded. A fresh
  // arrow each render re-ran that effect, which reloaded, which re-rendered:
  // deleting a speaker span an unbounded read loop over the speaker collection
  // and the whole roster. Every other section passes its hook's own reload,
  // which is already stable; this is the only combined one.
  const reloadBoth = useCallback(() => {
    speakers.reload();
    attendees.reload();
  }, [speakers.reload, attendees.reload]);

  return (
    <EntityCrudSection
      eventId={eventId}
      uid={uid}
      spec={speakerSpec}
      items={speakers.status === "success" ? sortSpeakers(speakers.data) : []}
      loading={speakers.status === "loading"}
      error={speakers.status === "error" ? speakers.error : undefined}
      reload={reloadBoth}
      lookups={{
        attendeeId: {
          options,
          loading: attendees.status === "loading",
          error:
            attendees.status === "error"
              ? `The roster could not be loaded, so there is nobody to link to: ${attendees.error}`
              : undefined,
          empty: "No attendee matches that.",
          // One attendee, one speaker card. Two speakers pointing at one
          // person would show them twice in the speaker list and hide them
          // from the agenda presenter picker under both ids.
          unavailable: (option, editingId) => {
            const holder = linkedBy.get(option.id);
            if (!holder || holder.id === editingId) return undefined;
            return `Already ${holder.name}`;
          },
        },
      }}
    />
  );
}
